import { digest, publicJwk } from "../../../../../supabase/functions/sync-control/policy";
import { writeCheckpoint } from "./checkpoints";
import { canonical, openDeviceEnvelope, sealDeviceEnvelope, unbase64url } from "./cryptoV2";
import { type DriveFileV2, GoogleDriveV2 } from "./driveV2";
import type { RecoveryDraft } from "./recoveryDraft";
import type { ManagementContext } from "./syncManagement";
import { dataKeyScope } from "./syncScopes";
import type { VerifyOperation } from "./useSyncReview";

export async function rebindDrive(
  context: ManagementContext,
  draft: RecoveryDraft,
  verify: VerifyOperation,
  backup: { receipt: string; backupChecksum: string; confirmedSubject: string },
) {
  const material = draft.verifiedMaterial();
  const { api, device, client, workspaceId } = context;
  const summary = await api.devices();
  const root = summary.root;
  if (!root || !summary.drive) throw new Error("sync-drive-unbound");
  const publicKey = publicJwk(draft.publicKey);
  const matching =
    root.datasetId === material.datasetId &&
    root.generation === material.generation &&
    root.keyVersion === material.keyVersion &&
    canonical(publicJwk(root.recoveryPublicKey)) === canonical(publicKey);
  if ((matching ? summary.drive.subject : summary.oauthCandidate?.subject) !== backup.confirmedSubject)
    throw new Error("sync-drive-confirmation-required");
  if (!matching) {
    if (
      !summary.oauthCandidate ||
      summary.oauthCandidate.subject === summary.drive.subject ||
      material.keyVersion !== 1 ||
      root.datasetId === material.datasetId ||
      root.generation === material.generation
    )
      throw new Error("sync-drive-confirmation-required");
    if ((await client.syncStats(workspaceId)).pending) throw new Error("sync-checkpoint-not-ready");
    await client.syncMaintenance(workspaceId, "drive-rebind");
    let switched = false;
    try {
      const local = await client.syncState(workspaceId);
      if (!local || local.status !== "active") throw new Error("sync-locked");
      const keyEnvelope = await sealDeviceEnvelope(
        await crypto.subtle.exportKey("jwk", device.agreement.publicKey),
        { ...dataKeyScope(material), objectId: device.deviceId },
        { dataKey: material.dataKey },
      );
      const nextRoot = {
        datasetId: material.datasetId,
        generation: material.generation,
        keyVersion: 1,
        recoveryPublicKey: publicKey,
        deviceEnvelopes: { [device.deviceId]: keyEnvelope },
      };
      const target = {
        confirmedSubject: summary.oauthCandidate.subject,
        backupChecksum: backup.backupChecksum,
        root: nextRoot,
      };
      api.clearTransport();
      await api.request("drive-rebind", { stage: "begin", ...target, proofId: await verify("rebind", target) });
      const drive = new GoogleDriveV2(() => api.driveToken(true));
      const inventory: DriveFileV2[] = [];
      let next: string | undefined;
      const cursors = new Set<string>();
      do {
        const page = await drive.list(next);
        inventory.push(...page.files);
        next = page.nextPageToken;
        if (next && cursors.has(next)) throw new Error("sync-drive-cursor");
        if (next) cursors.add(next);
      } while (next);
      const bytes = unbase64url(material.dataKey, 32);
      const key = await crypto.subtle.importKey("raw", bytes, "AES-GCM", false, ["encrypt", "decrypt"]);
      bytes.fill(0);
      const result = await writeCheckpoint(
        {
          clock: local.clock,
          page: (after) => client.syncCheckpoint(workspaceId, { action: "page", after, clock: local.clock }),
        },
        drive,
        nextRoot,
        key,
        device.signing.privateKey,
        device.deviceId,
        inventory,
      );
      const current = await client.syncState(workspaceId);
      if (
        !current ||
        current.datasetId !== local.datasetId ||
        current.generation !== local.generation ||
        current.maintenance !== "drive-rebind" ||
        canonical(current.clock) !== canonical(local.clock)
      )
        throw new Error("sync-checkpoint-stale");
      const cloudReceipt = await digest(result.pointer);
      await api.request("drive-rebind", {
        stage: "complete",
        checkpoint: result.pointer,
        cloudReceipt,
        recoverySignature: await draft.sign({
          accountId: device.accountId,
          datasetId: nextRoot.datasetId,
          generation: nextRoot.generation,
          cloudReceipt,
          checkpoint: result.pointer,
        }),
      });
      switched = true;
    } finally {
      if (!switched) await client.syncMaintenance(workspaceId, null).catch(() => undefined);
    }
  } else {
    const envelope = root.deviceEnvelopes[device.deviceId] as Awaited<ReturnType<typeof sealDeviceEnvelope>>;
    const value = (await openDeviceEnvelope(
      device.agreement.privateKey,
      { ...dataKeyScope(root), objectId: device.deviceId },
      envelope,
    )) as { dataKey: string };
    if (value.dataKey !== material.dataKey) throw new Error("sync-key-scope");
  }
  const unlocked = await context.vault.create(
    { accountId: device.accountId, deviceId: device.deviceId },
    dataKeyScope(material),
    material.dataKey,
    device.remembered,
    device,
  );
  await client.syncJoin(
    workspaceId,
    { datasetId: material.datasetId, generation: material.generation, keyVersion: 1, deviceId: device.deviceId },
    unlocked.dataKey,
    backup.receipt,
  );
  api.clearTransport();
  draft.dispose();
  return unlocked;
}
